// @flow
import { useFilters } from 'hooks/useQuery'
import useScreenSizeStyle from 'hooks/useScreenSizeStyle'
import {
  invalidateLoadReports,
  useLoadReports,
} from 'queries/reports/useLoadReports'
import * as React from 'react'
import { Link } from 'react-router-dom'
import Badge from 'ui/atoms/Badge'
import Table from 'ui/atoms/Table'
import Text from 'ui/atoms/Text'
import { getFormattedDate } from 'utils/dates'

function ReportsTable(): React.Node {
  const textSize = useScreenSizeStyle({
    md: { small: true, xsmall: false },
    generic: { small: false, xsmall: true },
  })

  const maxWidthText = useScreenSizeStyle({
    md: '170px',
    generic: '110px',
  })

  const {
    filters: { pageSize, ...filters },
  } = useFilters()
  const {
    data: reports,
    isLoading,
    isFetching,
  } = useLoadReports({ ...filters, page_size: pageSize })

  React.useEffect(() => {
    invalidateLoadReports()
  }, [])

  const reportsLoading = isLoading || isFetching

  const getStatusBadge = React.useCallback((status: string) => {
    if (status === 'Submitted') return <Badge $success text={status} />
    if (status === 'Pending') return <Badge text={status} />
    if (status === 'Undeliverable') return <Badge $error text={status} />
    return <Badge $warning text={status} />
  }, [])

  return (
    <Table>
      <Table.Head
        columns={[
          'CLIENT',
          'FORM',
          'SENT',
          'COMPLETED',
          'STATUS',
          'METHOD',
          'ARCHIVED',
        ]}
        // ["customer_name", "resource_name", "sent_at", "completed_at", "message_status", "preferred_delivery_method", "archived"]
        orderSet={[
          { column: 'CLIENT', key: 'customer_name' },
          { column: 'FORM', key: 'form_name' },
          { column: 'SENT', key: 'sent_at' },
          { column: 'COMPLETED', key: 'completed_at' },
          { column: 'STATUS', key: 'status' },
          { column: 'METHOD', key: 'method' },
          { column: 'ARCHIVED', key: 'archived' },
        ]}
      />
      <Table.Body>
        {reportsLoading && (
          <Table.Row>
            <Table.Column colspan={8}>Loading...</Table.Column>
          </Table.Row>
        )}
        {!reportsLoading && !reports?.results.length && (
          <Table.Row>
            <Table.Column colspan={8}>No Results Found</Table.Column>
          </Table.Row>
        )}
        {!reportsLoading &&
          reports?.results.map((report) => (
            <Table.Row key={report.id}>
              {/* <Table.Column>
                <Checkbox
                  noMargin
                  checked={isReportSelected(report)}
                  onChange={() => toggleSelectedReport(report)}
                />
              </Table.Column> */}
              <Table.Column>
                <Text
                  $truncate
                  $small={textSize.small}
                  $xsmall={textSize.xsmall}
                  style={{ maxWidth: maxWidthText }}
                >
                  {report.customer_is_anonymous ? (
                    <Text $muted>Anonymous</Text>
                  ) : (
                    <Link to={`/clients/${report.customer_id}`}>
                      {report.customer_name}
                    </Link>
                  )}
                </Text>
              </Table.Column>
              <Table.Column>
                <Text
                  $truncate
                  $small={textSize.small}
                  $xsmall={textSize.xsmall}
                  style={{ maxWidth: maxWidthText }}
                >
                  <Link to={`/response/${report?.uid}`}>
                    {report.document_name || report.form_name}
                  </Link>
                </Text>
              </Table.Column>
              <Table.Column>{getFormattedDate(report.sent_at)}</Table.Column>
              <Table.Column>
                {getFormattedDate(report.completed_at)}
              </Table.Column>
              <Table.Column>{getStatusBadge(report.status)}</Table.Column>
              <Table.Column>{report.method}</Table.Column>
              <Table.Column>{report.archived ? 'Yes' : 'No'}</Table.Column>
            </Table.Row>
          ))}
      </Table.Body>
    </Table>
  )
}

export default ReportsTable
